import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, ArrowRight, Calendar, Fuel, Gauge, Settings, Palette,
  CheckCircle2, ShieldCheck, Filter, X
} from 'lucide-react';
import { carsAPI } from '../services/api';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import ComingSoonPlaceholder from '../components/ComingSoonPlaceholder';
import CarGallery from '../components/CarGallery';
import FinancingCalculator from '@/components/financing/FinancingCalculator';
import CarCard from '../components/CarCard';
import Seo from '../components/Seo';

const statusBadgeCls = {
  available: 'badge-status badge-status--available',
  reserved: 'badge-status badge-status--reserved',
  sold: 'badge-status badge-status--sold',
  coming_soon: 'badge-status badge-status--soon',
};

const Inventory = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [cars, setCars] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filters
  const [status, setStatus] = useState('all');
  const [makeFilter, setMakeFilter] = useState(searchParams.get('make') || '');
  const [maxPrice, setMaxPrice] = useState(searchParams.get('maxPrice') || '');
  const [minYear, setMinYear] = useState('');
  const [maxKm, setMaxKm] = useState('');
  const [fuelFilter, setFuelFilter] = useState('all');
  const [sortBy, setSortBy] = useState('price-asc');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    carsAPI.getAll()
      .then((res) => { if (mounted) setCars(res.data || []); })
      .catch((err) => console.error(err))
      .finally(() => { if (mounted) setIsLoading(false); });
    return () => { mounted = false; };
  }, []);

  // Filtered list
  const filteredCars = useMemo(() => {
    let list = [...cars];
    if (status !== 'all') list = list.filter((c) => c.status === status);
    if (makeFilter.trim()) {
      const q = makeFilter.trim().toLowerCase();
      list = list.filter((c) => `${c.make} ${c.model}`.toLowerCase().includes(q));
    }
    if (maxPrice) list = list.filter((c) => (c.price || 0) <= Number(maxPrice));
    if (minYear) list = list.filter((c) => (c.year || 0) >= Number(minYear));
    if (maxKm) list = list.filter((c) => (c.mileage || 0) <= Number(maxKm));
    if (fuelFilter !== 'all') list = list.filter((c) => (c.fuel || '').toLowerCase() === fuelFilter.toLowerCase());

    switch (sortBy) {
      case 'price-asc': list.sort((a, b) => (a.price || 0) - (b.price || 0)); break;
      case 'price-desc': list.sort((a, b) => (b.price || 0) - (a.price || 0)); break;
      case 'year-new': list.sort((a, b) => (b.year || 0) - (a.year || 0)); break;
      case 'year-old': list.sort((a, b) => (a.year || 0) - (b.year || 0)); break;
      case 'mileage': list.sort((a, b) => (a.mileage || 0) - (b.mileage || 0)); break;
      default: break;
    }
    return list;
  }, [cars, status, makeFilter, maxPrice, minYear, maxKm, fuelFilter, sortBy]);

  const resetFilters = () => {
    setStatus('all'); setMakeFilter(''); setMaxPrice(''); setMinYear('');
    setMaxKm(''); setFuelFilter('all'); setSortBy('price-asc');
    setSearchParams({});
  };

  // Compute filtered list (top-level useMemo — must be before any early return)
  const fuelOptions = useMemo(() => {
    const set = new Set();
    cars.forEach((c) => c.fuel && set.add(c.fuel));
    return Array.from(set);
  }, [cars]);

  // ============ DETAIL VIEW ============
  if (id) {
    const car = cars.find((c) => c.id === id);

    if (isLoading) {
      return (
        <div className="min-h-screen bg-[#F7F8FA] pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="shimmer h-96 rounded-2xl" />
          </div>
        </div>
      );
    }

    if (!car) {
      return (
        <div className="min-h-screen bg-[#F7F8FA] pt-32 pb-20" data-testid="car-not-found">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl font-bold text-slate-900 mb-4">{t('car.notFound', 'Véhicule introuvable')}</h1>
            <Link to="/inventory" className="btn-primary">{t('car.backToInventory', "Retour à l'inventaire")}</Link>
          </div>
        </div>
      );
    }

    const allImages = [car.image, ...(car.images || [])].filter(Boolean);
    const isComingSoon = car.status === 'coming_soon';
    const showPlaceholder = isComingSoon && !car.image;

    const reserveSubject = `${t('car.reserveSubject', 'Réservation')} – ${car.year} ${car.make} ${car.model}`;
    const reserveMessage = `${t('car.reserveMessage', 'Bonjour, je souhaite réserver ce véhicule :')}\n\n• ${car.year} ${car.make} ${car.model}\n• ${car.price ? `${car.price.toLocaleString('fr-BE')} €` : ''}\n• ${typeof window !== 'undefined' ? window.location.origin : ''}/inventory/${car.id}`;
    const canReserve = car.status === 'available';

    // JSON-LD Vehicle schema for SEO
    const vehicleJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Vehicle',
      name: `${car.year} ${car.make} ${car.model}`,
      brand: { '@type': 'Brand', name: car.make },
      model: car.model,
      vehicleModelDate: String(car.year),
      productionDate: String(car.year),
      mileageFromOdometer: { '@type': 'QuantitativeValue', value: car.mileage, unitCode: 'KMT' },
      fuelType: car.fuel,
      vehicleTransmission: car.transmission,
      color: car.color,
      image: allImages.length ? allImages : undefined,
      description: (car.description_translations && car.description_translations[i18n.language]) || car.description,
      offers: car.price ? {
        '@type': 'Offer',
        priceCurrency: 'EUR',
        price: car.price,
        availability: car.status === 'available'
          ? 'https://schema.org/InStock'
          : car.status === 'reserved'
            ? 'https://schema.org/InStoreOnly'
            : car.status === 'sold'
              ? 'https://schema.org/SoldOut'
              : 'https://schema.org/PreOrder',
        seller: { '@type': 'AutomotiveBusiness', name: 'AutoDistrict' },
      } : undefined,
    };

    const seoTitle = `${car.year} ${car.make} ${car.model}${car.price ? ` — €${car.price.toLocaleString('fr-BE')}` : ''}`;
    const seoDesc = (car.description_translations && car.description_translations[i18n.language]) ||
      car.description || `${car.year} ${car.make} ${car.model}, ${(car.mileage || 0).toLocaleString('fr-BE')} km, ${car.fuel}. ${t('inventory.subtitle')}`;
    const seoImage = (car.image || (car.images && car.images[0])) || undefined;

    return (
      <div className="min-h-screen bg-[#F7F8FA] pt-28 lg:pt-32 pb-20" data-testid="car-detail-page">
        <Seo title={seoTitle} description={seoDesc.slice(0, 160)} image={seoImage} type="product" extraJsonLd={vehicleJsonLd} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/inventory" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 text-sm font-medium mb-8 transition-colors" data-testid="back-to-inventory">
            <ArrowLeft size={16} />
            {t('car.backToInventory', "Retour à l'inventaire")}
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-8 lg:gap-12">
            {/* Left: gallery */}
            <div>
              <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 relative">
                {showPlaceholder ? (
                  <div className="aspect-[4/3]">
                    <ComingSoonPlaceholder />
                  </div>
                ) : (
                  <CarGallery images={allImages} make={car.make} model={car.model} />
                )}
                <div className="absolute top-4 left-4 z-10">
                  <span className={statusBadgeCls[car.status]}>
                    {t(`common.${car.status}`)}
                  </span>
                </div>
              </div>

              {/* Description */}
              <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
                <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {t('car.description', 'Description')}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">
                  {(car.description_translations && car.description_translations[i18n.language]) || car.description}
                </p>
              </div>

              {/* Features */}
              {car.features && car.features.length > 0 && (
                <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
                  <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {t('car.features', 'Équipements')}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {((car.features_translations && car.features_translations[i18n.language]) || car.features).map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-slate-700 text-sm">
                        <CheckCircle2 className="text-emerald-500 flex-shrink-0" size={16} />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right: sticky price card */}
            <div className="lg:sticky lg:top-28 lg:self-start space-y-4">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-sm">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{car.year}</p>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 leading-tight" data-testid="car-detail-title" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {car.make} {car.model}
                </h1>
                <p className="text-4xl font-extrabold text-blue-900 mt-4 mb-4" data-testid="car-detail-price" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {!isComingSoon ? `€${(car.price || 0).toLocaleString('fr-BE')}` : t('coming.subline', 'À venir')}
                </p>

                <div className="badge-trust mb-5" style={{ display: car.warranty_included === false ? 'none' : undefined }}>
                  <ShieldCheck size={14} />
                  {t('card.warranty', 'Garantie 1 an incluse')}
                </div>

                <div className="grid grid-cols-2 gap-3 mb-6">
                  {[
                    { icon: Gauge, label: t('car.mileage', 'Kilométrage'), value: `${(car.mileage || 0).toLocaleString('fr-BE')} km` },
                    { icon: Fuel, label: t('car.fuel', 'Carburant'), value: car.fuel },
                    { icon: Settings, label: t('car.transmission', 'Boîte'), value: car.transmission },
                    { icon: Palette, label: t('car.color', 'Couleur'), value: car.color },
                  ].map((spec, i) => {
                    const Icon = spec.icon;
                    return (
                      <div key={i} className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                        <div className="flex items-center gap-1.5 text-slate-500 text-[11px] uppercase tracking-wide mb-1">
                          <Icon size={13} />
                          {spec.label}
                        </div>
                        <p className="text-slate-900 text-sm font-semibold capitalize">{spec.value || '—'}</p>
                      </div>
                    );
                  })}
                </div>

                <div className="space-y-2">
                  <Link to="/booking" state={{ car }} className="w-full btn-primary" data-testid="car-book-appointment">
                    <Calendar size={16} />
                    {t('car.bookToView', 'Prendre rendez-vous')}
                  </Link>
                  {canReserve && (
                    <button
                      type="button"
                      onClick={() => navigate(`/contact?subject=${encodeURIComponent(reserveSubject)}&message=${encodeURIComponent(reserveMessage)}`)}
                      className="w-full btn-success"
                      data-testid={`reserve-btn-${car.id}`}
                    >
                      {t('car.reserve', 'Réserver ce véhicule')}
                    </button>
                  )}
                  <Link to="/contact" className="w-full btn-secondary" data-testid="car-contact-us">
                    {t('car.contactInfo', 'Nous contacter')}
                  </Link>
                </div>
              </div>

              {/* Financing calculator — sidebar */}
              {!isComingSoon && car.price > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                  <FinancingCalculator
                    price={car.price}
                    year={car.year}
                    make={car.make}
                    model={car.model}
                    variant="sidebar"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============ LIST VIEW ============
  const FilterPanel = (
    <div className="space-y-5">
      <div>
        <label className="form-label">{t('filters.makeModel', 'Marque ou modèle')}</label>
        <input
          type="text"
          value={makeFilter}
          onChange={(e) => setMakeFilter(e.target.value)}
          placeholder="Audi, BMW, Golf…"
          className="form-input"
          data-testid="filter-make"
        />
      </div>
      <div>
        <label className="form-label">{t('filters.maxPrice', 'Budget max (€)')}</label>
        <input
          type="number"
          value={maxPrice}
          onChange={(e) => setMaxPrice(e.target.value)}
          placeholder="25 000"
          className="form-input"
          data-testid="filter-price"
        />
      </div>
      <div>
        <label className="form-label">{t('filters.minYear', 'Année min')}</label>
        <input
          type="number"
          value={minYear}
          onChange={(e) => setMinYear(e.target.value)}
          placeholder="2015"
          className="form-input"
          data-testid="filter-year"
        />
      </div>
      <div>
        <label className="form-label">{t('filters.maxKm', 'Km max')}</label>
        <input
          type="number"
          value={maxKm}
          onChange={(e) => setMaxKm(e.target.value)}
          placeholder="100 000"
          className="form-input"
          data-testid="filter-km"
        />
      </div>
      <div>
        <label className="form-label">{t('filters.fuel', 'Carburant')}</label>
        <Select value={fuelFilter} onValueChange={setFuelFilter}>
          <SelectTrigger className="form-input" data-testid="filter-fuel">
            <SelectValue placeholder={t('filters.all', 'Tous')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('filters.all', 'Tous')}</SelectItem>
            {fuelOptions.map((f) => (
              <SelectItem key={f} value={f.toLowerCase()} className="capitalize">{f}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="form-label">{t('filters.status', 'Statut')}</label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="form-input" data-testid="filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('inventory.all', 'Tous')}</SelectItem>
            <SelectItem value="available">{t('inventory.available', 'Disponible')}</SelectItem>
            <SelectItem value="reserved">{t('inventory.reserved', 'Réservé')}</SelectItem>
            <SelectItem value="coming_soon">{t('common.comingSoon', 'Bientôt')}</SelectItem>
            <SelectItem value="sold">{t('common.sold', 'Vendu')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <button
        type="button"
        onClick={resetFilters}
        className="w-full text-sm text-slate-600 hover:text-slate-900 underline underline-offset-4"
        data-testid="filter-reset"
      >
        {t('filters.reset', 'Réinitialiser les filtres')}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F7F8FA] pt-24 lg:pt-28 pb-20" data-testid="inventory-page">
      <Seo title={t('seo.inventory.title')} description={t('seo.inventory.desc')} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 lg:mb-10">
          <span className="section-eyebrow">{t('inventory.eyebrow', 'Sélection contrôlée')}</span>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900" data-testid="inventory-heading" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('inventory.inventory', 'Nos véhicules disponibles')}
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl">
            {t('inventory.subtitle', "Chaque véhicule est contrôlé, certifié Car-Pass et livré avec une garantie 1 an incluse.")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
          {/* Desktop filters sidebar */}
          <aside className="hidden lg:block" data-testid="filter-sidebar">
            <div className="sticky top-28 bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="text-base font-bold text-slate-900 mb-5 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Filter size={16} /> {t('filters.title', 'Filtres')}
              </h2>
              {FilterPanel}
            </div>
          </aside>

          {/* Mobile filters drawer */}
          {showFilters && (
            <div className="lg:hidden fixed inset-0 z-50 bg-black/40 flex items-end" onClick={() => setShowFilters(false)}>
              <div
                className="bg-white w-full max-h-[80vh] overflow-y-auto rounded-t-3xl p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-5">
                  <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('filters.title', 'Filtres')}</h2>
                  <button onClick={() => setShowFilters(false)} className="p-2 text-slate-500" aria-label="Close">
                    <X size={20} />
                  </button>
                </div>
                {FilterPanel}
              </div>
            </div>
          )}

          {/* Main content */}
          <main>
            {/* Top bar: result count + sort + mobile filter btn */}
            <div className="flex items-center justify-between gap-4 mb-6">
              <p className="text-sm text-slate-600">
                <span className="font-bold text-slate-900">{filteredCars.length}</span>{' '}
                {t('inventory.resultCount', 'véhicule(s)')}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowFilters(true)}
                  className="lg:hidden inline-flex items-center gap-2 text-sm font-semibold text-slate-700 border border-slate-200 bg-white px-4 py-2 rounded-full hover:border-slate-300 transition-colors"
                  data-testid="mobile-filter-toggle"
                >
                  <Filter size={14} />
                  {t('filters.title', 'Filtres')}
                </button>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[200px] h-10 bg-white border-slate-200 text-slate-700 text-sm rounded-full px-4" data-testid="sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="price-asc">{t('inventory.priceLowHigh', 'Prix croissant')}</SelectItem>
                    <SelectItem value="price-desc">{t('inventory.priceHighLow', 'Prix décroissant')}</SelectItem>
                    <SelectItem value="year-new">{t('inventory.yearNew', 'Plus récent')}</SelectItem>
                    <SelectItem value="year-old">{t('inventory.yearOld', 'Plus ancien')}</SelectItem>
                    <SelectItem value="mileage">{t('inventory.mileage', 'Km croissant')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Grid */}
            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="rounded-2xl border border-slate-200 overflow-hidden bg-white">
                    <div className="aspect-[4/3] shimmer" />
                    <div className="p-5 space-y-3">
                      <div className="h-5 w-3/4 shimmer rounded" />
                      <div className="h-7 w-1/2 shimmer rounded" />
                      <div className="h-4 w-full shimmer rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredCars.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center" data-testid="no-vehicles">
                <p className="text-slate-600 mb-4">{t('inventory.noVehicles', 'Aucun véhicule ne correspond à vos critères.')}</p>
                <button onClick={resetFilters} className="btn-secondary">{t('filters.reset', 'Réinitialiser les filtres')}</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="cars-grid">
                {filteredCars.map((car, i) => (
                  <CarCard key={car.id} car={car} priority={i < 6} />
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Inventory;
